/**
 * Video Player Component - Glass Morphism Design
 * Displays generated video with playback controls and download
 * Validates: Requirements 17.5
 */
import { useRef, useState } from 'react';
import { VideoJob } from '../../stores/videoStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface VideoPlayerProps {
  job: VideoJob | null;
  onClose?: () => void;
}

export function VideoPlayer({ job, onClose }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!job) {
    return null;
  }

  const video = job.video;

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = vol;
      setVolume(vol);
      setIsMuted(vol === 0);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (!isFullscreen) {
        if (videoRef.current.requestFullscreen) {
          videoRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
      setIsFullscreen(!isFullscreen);
    }
  };

  const handleDownload = async () => {
    if (!video?.url) return;

    try {
      const response = await fetch(video.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `video-${job.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab
      window.open(video.url, '_blank');
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Show placeholder if no video yet
  if (!video) {
    return (
      <div
        className="rounded-2xl p-6 border"
        style={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          borderColor: THEME.border,
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold" style={{ color: THEME.textMain }}>
            Video Preview
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors"
              style={{ color: THEME.textMuted }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.color = THEME.textMain;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = THEME.textMuted;
              }}
            >
              <Icons.X size={20} />
            </button>
          )}
        </div>

        <div
          className="aspect-video rounded-xl flex items-center justify-center"
          style={{ backgroundColor: THEME.bgSurface }}
        >
          {job.status === 'processing' ? (
            <div className="text-center">
              <Icons.Refresh
                size={48}
                className="animate-spin mx-auto mb-3"
                style={{ color: THEME.accentColor }}
              />
              <p style={{ color: THEME.textSecondary }}>Generating video...</p>
              {job.progress !== undefined && (
                <div className="mt-3 w-48 mx-auto">
                  <div
                    className="rounded-full h-2"
                    style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${job.progress}%`,
                        background: THEME.accent,
                      }}
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: THEME.textMuted }}>
                    {job.progress}% complete
                  </p>
                </div>
              )}
            </div>
          ) : job.status === 'failed' ? (
            <div className="text-center">
              <Icons.X
                size={48}
                className="mx-auto mb-3"
                style={{ color: THEME.error }}
              />
              <p style={{ color: THEME.error }}>Generation failed</p>
              {job.error && (
                <p className="text-xs mt-2 max-w-md" style={{ color: THEME.textMuted }}>
                  {job.error}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center">
              <Icons.Video
                size={48}
                className="mx-auto mb-3"
                style={{ color: THEME.textMuted }}
              />
              <p style={{ color: THEME.textMuted }}>Waiting for video...</p>
            </div>
          )}
        </div>

        {/* Job Info */}
        <div
          className="mt-4 p-3 rounded-xl"
          style={{ backgroundColor: THEME.bgSurface }}
        >
          <p className="text-sm mb-2" style={{ color: THEME.textMain }}>
            {job.enhancedPrompt || job.prompt}
          </p>
          <div className="flex items-center gap-3 text-xs" style={{ color: THEME.textMuted }}>
            <span>{job.model}</span>
            {job.settings.duration && <span>• {job.settings.duration}s</span>}
            {job.settings.resolution && <span>• {job.settings.resolution}</span>}
            {job.settings.aspectRatio && <span>• {job.settings.aspectRatio}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl p-6 border"
      style={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderColor: THEME.border,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold" style={{ color: THEME.textMain }}>
          Video Preview
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl transition-all duration-200"
            style={{
              background: THEME.accent,
              color: THEME.textMain,
              boxShadow: THEME.shadowAccent,
            }}
          >
            <Icons.Download size={16} />
            <span>Download</span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg transition-colors"
              style={{ color: THEME.textMuted }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
                e.currentTarget.style.color = THEME.textMain;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = THEME.textMuted;
              }}
            >
              <Icons.X size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Video Player */}
      <div className="relative aspect-video bg-black rounded-xl overflow-hidden group">
        <video
          ref={videoRef}
          src={video.url}
          poster={video.thumbnailUrl}
          className="w-full h-full object-contain"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />

        {/* Play/Pause Overlay */}
        <button
          onClick={handlePlayPause}
          className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {isPlaying ? (
            <div
              className="w-16 h-16 flex items-center justify-center rounded-full"
              style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            >
              <Icons.Play size={32} style={{ color: THEME.textMain }} />
            </div>
          ) : (
            <div
              className="w-16 h-16 flex items-center justify-center rounded-full"
              style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            >
              <Icons.Play size={32} style={{ color: THEME.textMain }} />
            </div>
          )}
        </button>

        {/* Controls */}
        <div
          className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
          }}
        >
          {/* Progress Bar */}
          <input
            type="range"
            min={0}
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1 rounded-lg appearance-none cursor-pointer mb-3"
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              accentColor: THEME.accentColor,
            }}
          />

          <div className="flex items-center justify-between">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              <button
                onClick={handlePlayPause}
                className="transition-colors"
                style={{ color: THEME.textMain }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = THEME.accentColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = THEME.textMain;
                }}
              >
                {isPlaying ? <Icons.Play size={24} /> : <Icons.Play size={24} />}
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleMuteToggle}
                  className="transition-colors"
                  style={{ color: THEME.textMain }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = THEME.accentColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = THEME.textMain;
                  }}
                >
                  <Icons.Settings size={20} />
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 rounded-lg appearance-none cursor-pointer"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    accentColor: THEME.accentColor,
                  }}
                />
              </div>

              {/* Time */}
              <span className="text-sm" style={{ color: THEME.textMain }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleFullscreen}
                className="transition-colors"
                style={{ color: THEME.textMain }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = THEME.accentColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = THEME.textMain;
                }}
              >
                <Icons.Settings size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Video Info */}
      <div
        className="mt-4 p-3 rounded-xl"
        style={{ backgroundColor: THEME.bgSurface }}
      >
        <p className="text-sm mb-2" style={{ color: THEME.textMain }}>
          {job.enhancedPrompt || job.prompt}
        </p>
        <div className="flex items-center gap-3 text-xs" style={{ color: THEME.textMuted }}>
          <span>{job.model}</span>
          {video.duration && <span>• {video.duration}s</span>}
          {video.resolution && <span>• {video.resolution}</span>}
          {job.settings.aspectRatio && <span>• {job.settings.aspectRatio}</span>}
        </div>
      </div>
    </div>
  );
}
